'use client';

import { c as _c } from "react/compiler-runtime";
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { getImageProps } from 'next/image';
import * as React from 'react';
import { cn } from '@/lib/utils';
const Avatar = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <AvatarPrimitive.Root ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
Avatar.displayName = AvatarPrimitive.Root.displayName;

// Extended props to support Next.js image optimization
type AvatarImageProps = React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & {
  useNextImage?: boolean;
  width?: number;
  height?: number;
  priority?: boolean;
  quality?: number;
};
const AvatarImage = ({
  ref,
  className,
  useNextImage = true,
  width = 40,
  height = 40,
  priority = true,
  quality,
  ...props
}: AvatarImageProps & {
  ref?: React.RefObject<React.ElementRef<typeof AvatarPrimitive.Image> | null>;
}) => {
  let optimizedProps: ReturnType<typeof getImageProps>['props'] | null = null;
  if (useNextImage && props.src && typeof props.src === 'string') {
    try {
      const result = getImageProps({
        src: props.src,
        alt: props.alt || '',
        width,
        height,
        priority,
        quality
      });
      optimizedProps = result.props;
    } catch (error) {
      // Fallback to regular image if Next.js optimization fails
      console.warn('Failed to optimize avatar image with Next.js:', error);
    }
  }
  if (optimizedProps) {
    return <AvatarPrimitive.Image ref={ref} className={cn('aspect-square h-full w-full', className)} {...props} {...optimizedProps} style={{
      ...optimizedProps.style,
      objectFit: 'cover',
      objectPosition: 'center'
    }} />;
  }

  // Regular Radix UI avatar image (fallback or when optimization is disabled)
  return <AvatarPrimitive.Image ref={ref} className={cn('aspect-square h-full w-full', className)} {...props} />;
};
AvatarImage.displayName = AvatarPrimitive.Image.displayName;
const AvatarFallback = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex h-full w-full items-center justify-center rounded-full bg-muted", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <AvatarPrimitive.Fallback ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
export { Avatar, AvatarFallback, AvatarImage };